/**
 * Delivery Policy Page - Chính sách giao nhận
 */

import { UnifiedHeader } from '../../components/UnifiedHeader';
import { UnifiedFooter } from '../../components/UnifiedFooter';
import { SEOHead } from '../../components/SEOHead';
import { getCanonicalUrl, getSiteUrl } from '../../utils/siteConfig';
import { StructuredData } from '../../components/StructuredData';
import { Zap, Monitor, Clock, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export function DeliveryPolicyPage() {
  return (
    <div className='bg-background-light dark:bg-background-dark text-text-main-light dark:text-text-main-dark font-body transition-colors duration-200 min-h-screen flex flex-col'>
      <SEOHead
        title='Chính sách giao nhận | Hướng nghiệp CAT'
        description='Chính sách cấp quyền truy cập sản phẩm số tại Hướng nghiệp CAT'
        canonicalUrl={getCanonicalUrl('/chinh-sach-giao-nhan')}
      />
      <StructuredData
        data={{
          type: 'BreadcrumbList',
          items: [
            { name: 'Trang chủ', url: getSiteUrl() },
            { name: 'Chính sách giao nhận', url: getCanonicalUrl('/chinh-sach-giao-nhan') },
          ],
        }}
      />
      <UnifiedHeader />

      <motion.main
        className='flex-1 pt-20 pb-12'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          {/* Header */}
          <div className='text-center mb-12'>
            <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
              Chính sách giao nhận
            </h1>
            <p className='text-lg text-gray-600 dark:text-gray-300'>
              Cập nhật lần cuối: Tháng 12/2024
            </p>
          </div>

          <div className='space-y-8'>
            {/* Digital Product Notice */}
            <div className='bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-800'>
              <div className='flex items-start gap-4'>
                <Monitor className='w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1' />
                <div>
                  <h3 className='font-semibold text-blue-800 dark:text-blue-300 mb-2'>
                    Sản phẩm số - Truy cập trực tuyến
                  </h3>
                  <p className='text-blue-700 dark:text-blue-400'>
                    Tất cả sản phẩm của Hướng nghiệp CAT là <strong>sản phẩm số</strong>, được cung
                    cấp và truy cập hoàn toàn trực tuyến. Không có sản phẩm Vật lí cần giao nhận.
                  </p>
                </div>
              </div>
            </div>

            {/* Instant Access */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center'>
                  <Zap className='w-6 h-6 text-green-600 dark:text-green-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                  Cấp quyền truy cập ngay lập tức
                </h2>
              </div>

              <div className='prose dark:prose-invert max-w-none'>
                <p className='text-gray-600 dark:text-gray-300 mb-4'>
                  Sau khi thanh toán thành công, quyền truy cập sẽ được cấp{' '}
                  <strong>ngay lập tức</strong>:
                </p>

                <ul className='space-y-4'>
                  <li className='flex items-start gap-3'>
                    <div className='w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5'>
                      <span className='text-white text-sm'>✓</span>
                    </div>
                    <div>
                      <strong className='text-gray-900 dark:text-white'>Bài thi thử THPT:</strong>
                      <span className='text-gray-600 dark:text-gray-300'>
                        {' '}
                        Mở khóa và có thể làm bài ngay sau thanh toán
                      </span>
                    </div>
                  </li>
                  <li className='flex items-start gap-3'>
                    <div className='w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5'>
                      <span className='text-white text-sm'>✓</span>
                    </div>
                    <div>
                      <strong className='text-gray-900 dark:text-white'>
                        Báo cáo hướng nghiệp:
                      </strong>
                      <span className='text-gray-600 dark:text-gray-300'>
                        {' '}
                        Truy cập báo cáo đầy đủ ngay khi thanh toán hoàn tất
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Access Duration */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center'>
                  <Clock className='w-6 h-6 text-purple-600 dark:text-purple-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                  Thời hạn sử dụng
                </h2>
              </div>

              <div className='space-y-4'>
                <div className='p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl'>
                  <h3 className='font-semibold text-gray-900 dark:text-white mb-1'>
                    Bài thi thử THPT
                  </h3>
                  <p className='text-gray-600 dark:text-gray-300'>
                    Sử dụng không giới hạn thời gian trong thời gian tài khoản hoạt động
                  </p>
                </div>

                <div className='p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl'>
                  <h3 className='font-semibold text-gray-900 dark:text-white mb-1'>
                    Báo cáo hướng nghiệp CAT
                  </h3>
                  <p className='text-gray-600 dark:text-gray-300'>
                    Truy cập vĩnh viễn, có thể xem lại bất cứ lúc nào
                  </p>
                </div>
              </div>
            </div>

            {/* How to Access */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center'>
                  <HelpCircle className='w-6 h-6 text-amber-600 dark:text-amber-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                  Cách truy cập nội dung đã mua
                </h2>
              </div>

              <ol className='space-y-4'>
                {[
                  'Đăng nhập vào tài khoản Hướng nghiệp CAT',
                  'Truy cập mục "Lịch sử đánh giá" hoặc "Thư viện đề thi"',
                  'Các nội dung đã mở khóa sẽ được đánh dấu và có thể truy cập ngay',
                ].map((step, index) => (
                  <li key={index} className='flex gap-4'>
                    <div className='w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0'>
                      {index + 1}
                    </div>
                    <p className='text-gray-600 dark:text-gray-300 pt-1'>{step}</p>
                  </li>
                ))}
              </ol>
            </div>

            {/* Contact */}
            <div className='bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-6'>
              <h3 className='font-semibold text-gray-900 dark:text-white mb-2'>
                Không thể truy cập nội dung sau thanh toán?
              </h3>
              <p className='text-gray-600 dark:text-gray-300'>
                Vui lòng liên hệ: <strong>support@huongnghiep.cat.edu.vn</strong> - Chúng tôi sẽ hỗ
                trợ trong vòng 24 giờ làm việc.
              </p>
            </div>
          </div>
        </div>
      </motion.main>

      <UnifiedFooter />
    </div>
  );
}

export default DeliveryPolicyPage;
